import React, { useState, useEffect } from "react";

import ModalPage from "components/explore/layout/ModalPage";
import { makeStyles } from "@material-ui/styles";
import { Grid, Typography, Avatar, Link, Tooltip, CircularProgress } from '@material-ui/core';
import { Paging, Scrolling, Column } from 'devextreme-react/data-grid';
import { setExplorePropertyDetailsIsOpen, getPropertyDetailsDataForProspect, appNotify } from 'store/actions';
import { connect } from "react-redux";
import { compose } from "recompose";
import { LabeledBadge, BadgeList } from '@insightlabs/mui-labeled-badge';
import { formatValue } from 'utilities/format';
import DataGrid from 'components/devExtreme/DataGrid';
import Stack from "components/layout/Stack";
import PropertyDetailsStreetView from "components/explore/propertyDetailsPage/propertyDetailsStreetView";
import PropertyDetailsMap from "components/explore/propertyDetailsPage/propertyDetailsMap";
import PropertyDetailsTitle from "components/explore/propertyDetailsPage/propertyDetailsTitle";
import PropertyDetailsIndicativeItem from "components/explore/propertyDetailsPage/propertyDetailsIndicativeItem";
import DescriptiveFitIcon from "components/explore/propertyDetailsPage/DescriptiveFitIcon";
import VerticalDetailsBox from "components/explore/common/VerticalDetailsBox";
import { GetSchools } from "api/rex/schoolsData";
import { GetEventHistory } from "api/rex/eventHistory";
import { GetUnderwrite } from "api/revue/underwriteData";
import PropertyProforma from "components/explore/propertyDetailsPage/propertyProforma";
import Paper from '@material-ui/core/Paper';
import Popover from '@material-ui/core/Popover';
import IconButton from '@material-ui/core/IconButton';
import { NavigateNext, NavigateBefore } from "@material-ui/icons";
import { sendClientLog } from 'services/logService';
import { useJsApiLoader } from "@react-google-maps/api";
import { lastSelectedClusterSelector } from 'store/selectors';
import PropertyHistory from 'components/underwrite/acquisition/propertyUnderwrite/header/propertyHistory/PropertyHistory';
import { googleMapsApiKey } from "index";
import { LoadingOverlayWrapper } from 'components/layout';

const useStyles = makeStyles(theme => ({
  spacer: {
    height: 20
  },
  avatar: {
    backgroundColor: theme.palette.secondary.main,
  },
  titleRightBox: {
    position: 'absolute',
    right: 50
  },
  listItemText: {
    whiteSpace: 'nowrap',
    width: 150,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  spanOthers: {
    paddingHorizontal: 5
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 64
  },
  contentLoadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'calc(100vh - 160px)'
  }
}));

const scriptLibraries = ['geometry', 'drawing', 'places'];

function PropertyDetailsPage({ explorePropertyDetailsData, prospectsPropertyDetailsData, lastSelectedCluster,
  setExplorePropertyDetailsIsOpen, explorePropertyDetailsIsOpen, handleCompsPdoClick, appNotify, onClose,
  prospectProperty, prospectsPropertyDetailsIsOpen, isMobile, isLoading }) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
    libraries: scriptLibraries,
    version: '3.64'
  });
  const classes = useStyles();
  const [schoolsData, setSchoolsData] = useState(null);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [eventHistory, setEventHistory] = useState(null);
  const [eventHistoryLoading, setEventHistoryLoading] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);  // This is for proforma popover
  const [propertyUnderwriteStatus, setPropertyUnderwriteStatus] = useState(null);
  const [propertyUnderwriteLoading, setPropertyUnderwriteLoading] = useState(false);

  const schoolsGridRef = React.useRef(null);
  const lastEventHistory = eventHistory?.length
    ? eventHistory.slice().sort((a, b) => new Date(b.date) - new Date(a.date))[0]
    : null;

  useEffect(() => {
    if (!prospectsPropertyDetailsIsOpen) return;

    setEventHistory(null);
    setSchoolsData(null);
    setPropertyUnderwriteStatus(null);

    sendClientLog('Info', `User opened Property Details page from Prospects grid for PM ID ${prospectProperty.asgPropID}, Prospect ID ${prospectProperty.id}`);
    if (prospectsPropertyDetailsData) {
      loadSchools(prospectsPropertyDetailsData);
      loadEventHistory(prospectsPropertyDetailsData);
      if (prospectProperty?.transactionOpportunityId) {
        loadUnderwrite(prospectProperty.transactionOpportunityId);
      }
    }
  }, [prospectsPropertyDetailsIsOpen])
  useEffect(() => {
    if (!explorePropertyDetailsIsOpen) return;
    // get property details from explore map

    setEventHistory(null);
    setSchoolsData(null);
    setPropertyUnderwriteStatus(null);
    if (lastSelectedCluster) {
      loadSchools(explorePropertyDetailsData);
    }
  }, [explorePropertyDetailsIsOpen]);

  function onModalClose() {
    setExplorePropertyDetailsIsOpen(false)
    if (onClose) {
      onClose();
    }
  }

  function StatusBadge(propertyDetailsData) {
    if (eventHistoryLoading) {
      return <CircularProgress key={0} size={24} color="secondary" />
    }
    if (propertyDetailsData.status?.toLowerCase() === "for sale") {
      return (
        <LabeledBadge
          label="For Sale"
          value={
            (lastEventHistory ? formatValue(lastEventHistory.price, {
              type: 'money',
              decimals: 0
            }) : "N/A")
          }
          color="#048e00"
        />
      )
    } else if (propertyDetailsData.status?.toLowerCase() === "for rent") {
      return (
        <LabeledBadge
          label="For Rent"
          value={
            (lastEventHistory ? formatValue(lastEventHistory.price, {
              type: 'money',
              decimals: 0
            }) : "N/A")
          }
        />
      )
    } else if (lastEventHistory) {
      return (
        <LabeledBadge
          label={lastEventHistory.event}
          value={formatValue(lastEventHistory.price, {
            type: 'money',
            decimals: 0
          })}
          color="#757575"
        />
      )
    }
    return (<></>)
  }

  function OtherDetails(propertyDetailsData) {
    var label = "";
    if (propertyDetailsData.pool === "Y") {
      label += " Pool, ";
    } else if (propertyDetailsData.pool === "N") {
      label += " No Pool, ";
    }
    if (propertyDetailsData.hoa === "Y") {
      label += " HOA, ";
    } else if (propertyDetailsData.hoa === "N") {
      label += " No HOA, ";
    }
    if (propertyDetailsData.sfha === "Y") {
      label += " SFHA, ";
    } else if (propertyDetailsData.sfha === "N") {
      label += " No SFHA, ";
    }
    if (label !== "") {
      label = label.replace(/,\s*$/, "");
    } else if (!propertyDetailsData.constructionQuality) { // If no other details and no construction quality, show N/A
      label = 'N/A';
    }
    return label;
  }

  function SchoolsCell_Rating(cellData) {
    return (
      <Avatar className={classes.avatar}>
        {cellData.data.rating}
      </Avatar>
    );
  }

  function SchoolsCell_Name(cellData) {
    return (
      <Link href={cellData.data.overviewURL} target="_blank" rel="noopener">
        {cellData.data.name}
      </Link>
    );
  }

  function SchoolsCell_Distance(cellData) {
    return (
      <span>
        {formatValue(cellData.data.distanceFromTarget, { type: 'fixed', decimals: 2 })} mi
      </span>
    );
  }

  function loadUnderwrite(txnOppId) {
    setPropertyUnderwriteLoading(true);
    GetUnderwrite(txnOppId).then(res => {
      if (!res) {
        appNotify('Underwrite not found', 'warning');
      } else {
        setPropertyUnderwriteStatus(res);
      }
      setPropertyUnderwriteLoading(false);
    }).catch(error => {
      appNotify(error.message, 'error');
      setPropertyUnderwriteLoading(false);
    })
  }

  function loadSchools(propertyDetailsData) {
    if (propertyDetailsData) {
      setSchoolsLoading(true);
      GetSchools(propertyDetailsData.latitude, propertyDetailsData.longitude, true).then(schools => {
        setSchoolsData(schools);
        setSchoolsLoading(false);
      }).catch(error => {
        appNotify(error.message, 'error');
        setSchoolsLoading(false);
      })
    }
  }

  function loadEventHistory(propertyDetailsData) {
    if (propertyDetailsData && propertyDetailsData.livingAreaSqFt != null) {
      setEventHistoryLoading(true);
      GetEventHistory(propertyDetailsData.asgPropID, propertyDetailsData.livingAreaSqFt).then(eventHistory => {
        setEventHistory(eventHistory?.data);
        setEventHistoryLoading(false);
      }).catch(error => {
        appNotify(error.message, 'error');
        setEventHistory(null);
        setEventHistoryLoading(false);
      })
    } else {
      setEventHistory(null);
    }
  }

  const handlePopoverBtnClick = (event) => {
    sendClientLog('Info', `Explore - User opened proforma for explore property id ${lastSelectedCluster?.clusterID}`);
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const onCompsPdoClick = () => {
    handleCompsPdoClick();
    onModalClose();
  }
  
  const openPopover = Boolean(anchorEl);
  const id = openPopover ? 'proforma-popover' : undefined;
  if (!isLoaded) return null;
  if (!explorePropertyDetailsIsOpen && !prospectsPropertyDetailsIsOpen) return null;
  // If viewing from explore, explorePropertyDetailsData will be populated. If viewing from prospects, prospectsPropertyDetailsData will be populated if a GUD record is found, else we use the prospects grid data (most fields will be blank)
  const propertyDetailsData = explorePropertyDetailsIsOpen ? explorePropertyDetailsData : prospectsPropertyDetailsData;
  if (!propertyDetailsData && !prospectProperty) return null; // If propertyDetailsData is null, prospectProperty must not be null
  const buyBoxFitString = `${propertyDetailsData?.descriptiveBuyBoxFit ?? propertyUnderwriteStatus?.descriptiveFit ?? 'N/A'}`;
  const iconText = isMobile ? "" : "🏠";
  return (
    <ModalPage
      maxWidth="lg"
      open={explorePropertyDetailsIsOpen || prospectsPropertyDetailsIsOpen}
      fullScreen={isMobile}
      title={
        <PropertyDetailsTitle
          isMobile={isMobile}
          explorePropertyDetailsIsOpen={explorePropertyDetailsIsOpen}
          title={propertyDetailsData
            ? (iconText + " " + propertyDetailsData.fullAddress)
            : prospectProperty
              ? (iconText + "🏠 " + prospectProperty.streetAddress + " from Prospects")
              : (iconText + "🏠 Property Details")
          }
          zillowUrl={propertyDetailsData
            ? `http://www.zillow.com/homes/` + propertyDetailsData.address + `", "` + propertyDetailsData.zipCode + `_rb/`
            : `http://www.zillow.com/homes/` + prospectProperty.streetAddress + `", "` + prospectProperty.zipCode + `_rb/`
          }
          isLoadingPopupData={schoolsLoading || eventHistoryLoading || propertyUnderwriteLoading}
          badgeContent={<>
            {propertyDetailsData &&
              <DescriptiveFitIcon
                title={`Descriptive Fit: ${buyBoxFitString.charAt(0).toUpperCase() + buyBoxFitString.slice(1)}`}
                style={{
                  fill: (propertyDetailsData.descriptiveBuyBoxFit || propertyUnderwriteStatus?.descriptiveFit) ? 'green' : 'gray',
                  marginRight: 4
                }}
              />
            }
            {
              propertyDetailsData?.isAmherstManaged &&
              <LabeledBadge label="Amherst Property" value="Yes" color="secondary" />
            }
            {
              (propertyDetailsData?.isProspect || (prospectProperty?.asgPropID && (prospectProperty?.status !== 'Rejected' && prospectProperty?.status !== 'Closed'))) &&
              <LabeledBadge label="Prospect" value="Yes" color="secondary" />
            }
            {propertyDetailsData &&
              <BadgeList >
                {
                  StatusBadge(propertyDetailsData)
                }
              </BadgeList>
            }
          </>}
          onCompsPdoClick={onCompsPdoClick}
        />
      }
      onClose={onModalClose}
      disableBackdrop={false}
    >
      {isLoading
        ? <div className={classes.contentLoadingContainer}>
            <CircularProgress />
          </div>
        : <Stack grow>
            <PropertyDetailsStreetView propertyDetailsData={propertyDetailsData || prospectProperty} appNotify={appNotify} />
            <div className={classes.spacer} />
            <Typography variant="h6">Details</Typography>
            <Grid container>
              <PropertyDetailsIndicativeItem
                iconPath='M19,7H5V14H3V5H1V20H3V17H21V20H23V11A4,4 0 0,0 19,7'
                primaryText='Beds'
                secondaryText={propertyDetailsData
                  ? propertyDetailsData.beds ? (propertyDetailsData.beds === 1 ? propertyDetailsData.beds + " bedroom" : propertyDetailsData.beds + " bedrooms") : 'N/A'
                  : prospectProperty.beds ? (prospectProperty.beds === 1 ? prospectProperty.beds + " bedroom" : prospectProperty.beds + " bedrooms") : 'N/A'
                }
              />
              <PropertyDetailsIndicativeItem
                iconPath='M21,14V15C21,16.91 19.93,18.57 18.35,19.41L19,22H17L16.5,20C16.33,20 16.17,20 16,20H8C7.83,20 7.67,20 7.5,20L7,22H5L5.65,19.41C4.07,18.57 3,16.91 3,15V14H2V12H20V5A1,1 0 0,0 19,4C18.5,4 18.12,4.34 18,4.79C18.63,5.33 19,6.13 19,7H13A3,3 0 0,1 16,4C16.06,4 16.11,4 16.17,4C16.58,2.84 17.69,2 19,2A3,3 0 0,1 22,5V14H21V14M19,14H5V15A3,3 0 0,0 8,18H16A3,3 0 0,0 19,15V14Z'
                primaryText='Baths'
                secondaryText={propertyDetailsData
                  ? (propertyDetailsData.fullBaths || propertyDetailsData.halfBaths) ? (propertyDetailsData.fullBaths + " full" + (propertyDetailsData.halfBaths ? ", " + propertyDetailsData.halfBaths + " halfs" : "")) : 'N/A'
                  : prospectProperty.baths ? (prospectProperty.baths === 1 ? prospectProperty.beds + ' bath' : prospectProperty.baths + ' baths') : 'N/A'
                }
              />
              <PropertyDetailsIndicativeItem
                iconPath='M19,4H18V2H16V4H8V2H6V4H5C3.89,4 3,4.9 3,6V20A2,2 0 0,0 5,22H19A2,2 0 0,0 21,20V6A2,2 0 0,0 19,4M19,20H5V10H19V20M19,8H5V6H19V8M12,13H17V18H12V13Z'
                primaryText='Year Built'
                secondaryText={propertyDetailsData
                  ? propertyDetailsData.yearBuilt + (propertyDetailsData.effectiveYearBuilt ? ` (effective ${propertyDetailsData.effectiveYearBuilt})` : ``)
                  : prospectProperty.yearBuilt ?? 'N/A'
                }
              />
              <PropertyDetailsIndicativeItem
                iconPath='M3,5V21H9V19.5H7V18H9V16.5H5V15H9V13.5H7V12H9V10.5H5V9H9V5H10.5V9H12V7H13.5V9H15V5H16.5V9H18V7H19.5V9H21V3H5A2,2 0 0,0 3,5M6,7A1,1 0 0,1 5,6A1,1 0 0,1 6,5A1,1 0 0,1 7,6A1,1 0 0,1 6,7Z'
                primaryText='Living Area'
                secondaryText={propertyDetailsData
                  ? formatValue(propertyDetailsData.livingAreaSqFt, { type: 'fixed', decimals: 0 }) + " square feet"
                  : prospectProperty.sqft ? formatValue(prospectProperty.sqft, { type: 'fixed', decimals: 0 }) + " square feet" : 'N/A'
                }
              />
            </Grid>
            <Grid container>
              <PropertyDetailsIndicativeItem
                iconPath='M10,2V4.26L12,5.59V4H22V19H17V21H24V2H10M7.5,5L0,10V21H15V10L7.5,5M14,6V6.93L15.61,8H16V6H14M18,6V8H20V6H18M7.5,7.5L13,11V19H10V13H5V19H2V11L7.5,7.5M18,10V12H20V10H18M18,14V16H20V14H18Z'
                primaryText='Property Type'
                secondaryText={propertyDetailsData
                  ? propertyDetailsData.propertyType + (propertyDetailsData.propertySubtype ? " / " + propertyDetailsData.propertySubtype : "") +
                    (propertyDetailsData.mfUnits ? ` (${propertyDetailsData.mfUnits} unit${(propertyDetailsData.mfUnits !== 1 ? "s" : "")})` : ``)
                  : 'N/A'
                }
              />
              <PropertyDetailsIndicativeItem
                iconPath='M12 3L4 9V21H20V9L12 3M10 10H14V19H10V10M16 10H18V13H16V10M15.33 8H8.67L12 5.5L15.33 8M8 10V13H6V10H8M6 15H8V19H6V15M16 19V15H18V19H16Z'
                primaryText='Building Type'
                secondaryText={propertyDetailsData?.buildingType ?? 'N/A'}
              />
              <PropertyDetailsIndicativeItem
                iconPath='M19,14.5C19,14.5 21,16.67 21,18A2,2 0 0,1 19,20A2,2 0 0,1 17,18C17,16.67 19,14.5 19,14.5M5,18V9A2,2 0 0,1 3,7A2,2 0 0,1 5,5V4A2,2 0 0,1 7,2H9A2,2 0 0,1 11,4V5H19A2,2 0 0,1 21,7V9L21,11A1,1 0 0,1 22,12A1,1 0 0,1 21,13H17A1,1 0 0,1 16,12A1,1 0 0,1 17,11V9H11V18H12A2,2 0 0,1 14,20V22H2V20A2,2 0 0,1 4,18H5Z'
                primaryText='Water'
                secondaryText={propertyDetailsData?.waterType ?? 'N/A'}
              />
              <PropertyDetailsIndicativeItem
                iconPath='M6,21V8A2,2 0 0,1 8,6L16,3V6A2,2 0 0,1 18,8V21H12V16H8V21H6M14,19H16V16H14V19M8,13H10V9H8V13M12,13H16V9H12V13Z'
                primaryText='Stories'
                secondaryText={propertyDetailsData?.stories ?? 'N/A'}
              />
            </Grid>
            <Grid container>
              <PropertyDetailsIndicativeItem
                iconPath='M19,20H17V11H7V20H5V9L12,5L19,9V20M8,12H16V14H8V12M8,15H16V17H8V15M16,18V20H8V18H16Z'
                primaryText='Garage'
                secondaryText={propertyDetailsData
                  ? (propertyDetailsData.garage === "Y" ? "Yes" : (propertyDetailsData.garage === "N" ? "No garage" : "N/A")) + (propertyDetailsData.garageSpaces ? " (" + propertyDetailsData.garageSpaces + " space)" : "")
                  : 'N/A'
                }
              />
              <PropertyDetailsIndicativeItem
                iconPath='M12,18.54L19.37,12.8L21,14.07L12,21.07L3,14.07L4.62,12.81L12,18.54M12,16L3,9L12,2L21,9L12,16M12,4.53L6.26,9L12,13.47L17.74,9L12,4.53Z'
                primaryText='Lot Size'
                secondaryText={propertyDetailsData
                  ? <span>
                      {formatValue(propertyDetailsData.lotSizeAcres, { type: 'fixed', decimals: 2 }) + " acres"}
                      {
                        propertyDetailsData.lotFeatures &&
                        <Tooltip title={propertyDetailsData.lotFeatures} placement="bottom">
                          <span style={{ marginLeft: 5 }}>ⓘ</span>
                        </Tooltip>
                      }
                    </span>
                  : 'N/A'
                }
              />
              <PropertyDetailsIndicativeItem
                iconPath='M20 2H4C2.9 2 2 2.9 2 4V20C2 21.11 2.9 22 4 22H20C21.11 22 22 21.11 22 20V4C22 2.9 21.11 2 20 2M4 6L6 4H10.9L4 10.9V6M4 13.7L13.7 4H18.6L4 18.6V13.7M20 18L18 20H13.1L20 13.1V18M20 10.3L10.3 20H5.4L20 5.4V10.3Z'
                primaryText='Zoning'
                secondaryText={propertyDetailsData?.zoning ?? 'N/A'}
              />
              <PropertyDetailsIndicativeItem
                iconPath='M12,3L2,12H5V20H19V12H22L12,3M10,8H14V18H12V10H10V8Z'
                primaryText='Primary Use'
                secondaryText={propertyDetailsData?.countyLandUseCode1 ?? 'N/A'}
              />
            </Grid>
            <Grid container>
              <PropertyDetailsIndicativeItem
                iconPath='M12,3L2,12H5V20H19V12H22L12,3M9,8H13A2,2 0 0,1 15,10V12A2,2 0 0,1 13,14H11V16H15V18H9V14A2,2 0 0,1 11,12H13V10H9V8Z'
                primaryText='Secondary Use'
                secondaryText={propertyDetailsData?.countyLandUseCode2 ?? 'N/A'}
              />
              <PropertyDetailsIndicativeItem
                iconPath='M6.45,17.45L1,12L6.45,6.55L7.86,7.96L4.83,11H19.17L16.14,7.96L17.55,6.55L23,12L17.55,17.45L16.14,16.04L19.17,13H4.83L7.86,16.04L6.45,17.45Z'
                primaryText='Frontage'
                secondaryText={propertyDetailsData?.frontFootage
                  ? formatValue(propertyDetailsData.frontFootage, { type: 'fixed', decimals: 0 }) + " feet"
                  : 'N/A'
                }
              />
              <PropertyDetailsIndicativeItem
                iconPath='M17.45,17.55L12,23L6.55,17.55L7.96,16.14L11,19.17V4.83L7.96,7.86L6.55,6.45L12,1L17.45,6.45L16.04,7.86L13,4.83V19.17L16.04,16.14L17.45,17.55Z'
                primaryText='Depth'
                secondaryText={propertyDetailsData?.depthFootage
                  ? formatValue(propertyDetailsData.depthFootage, { type: 'fixed', decimals: 0 }) + " feet"
                  : 'N/A'
                }
              />
              <PropertyDetailsIndicativeItem
                iconPath='M16,12A2,2 0 0,1 18,10A2,2 0 0,1 20,12A2,2 0 0,1 18,14A2,2 0 0,1 16,12M10,12A2,2 0 0,1 12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12M4,12A2,2 0 0,1 6,10A2,2 0 0,1 8,12A2,2 0 0,1 6,14A2,2 0 0,1 4,12Z'
                primaryText='Other'
                secondaryText={propertyDetailsData
                  ? <span>
                      {OtherDetails(propertyDetailsData)}
                      {
                        propertyDetailsData.constructionQuality && propertyDetailsData.constructionQuality !== "" &&
                        <Tooltip title={'Construction Quality: ' + propertyDetailsData.constructionQuality} placement="bottom">
                          <span style={{ marginLeft: 5 }}>🔧</span>
                        </Tooltip>
                      }
                    </span>
                  : 'N/A'
                }
              />
            </Grid>
            <div className={classes.spacer} />
            {propertyDetailsData &&
              <div>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Typography variant="h6">Valuation</Typography>
                  {
                    propertyDetailsData.capRate ?
                      <div onClick={handlePopoverBtnClick} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: "pointer" }}>
                        <LabeledBadge
                          aria-describedby={id}
                          label="Cap Rate"
                          height="14"
                          value={
                            (propertyDetailsData ? formatValue(propertyDetailsData.capRate, {
                              type: 'percent',
                              decimals: 2
                            }) : "N/A")
                          }
                          color="primary"
                        />
                        <IconButton color="primary"
                          size="small">
                          {openPopover ? <NavigateBefore /> : <NavigateNext />}
                        </IconButton>
                      </div>
                      : null
                  }
                  <Popover
                    id={id}
                    open={openPopover}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <Paper elevation={4} style={{ padding: 10, width: '280px' }}>
                      <PropertyProforma propertyDetailsData={propertyDetailsData} />
                    </Paper>
                  </Popover>
                </div>
              </div>
            }
            <Grid container>
              <PropertyDetailsIndicativeItem
                iconPath='M7,15H9C9,16.08 10.37,17 12,17C13.63,17 15,16.08 15,15C15,13.9 13.96,13.5 11.76,12.97C9.64,12.44 7,11.78 7,9C7,7.21 8.47,5.69 10.5,5.18V3H13.5V5.18C15.53,5.69 17,7.21 17,9H15C15,7.92 13.63,7 12,7C10.37,7 9,7.92 9,9C9,10.1 10.04,10.5 12.24,11.03C14.36,11.56 17,12.22 17,15C17,16.79 15.53,18.31 13.5,18.82V21H10.5V18.82C8.47,18.31 7,16.79 7,15Z'
                primaryText='AVM'
                secondaryText={propertyDetailsData
                  ? formatValue(propertyDetailsData.avmValue, { type: 'money', decimals: 0 }) + " (" + formatValue(propertyDetailsData.valueDate, { type: 'date' }) + ")"
                  : 'N/A'
                }
              />
              <PropertyDetailsIndicativeItem
                iconPath='M3,13H7V23H3V13M10,14H14V23H10V14M17,9H21V23H17V9M17,1H21V5H20V3.06L11.97,11.09L8,7.12L3.4,11.72L2.34,10.66L8,5L11.97,8.97L18.94,2H17V1Z'
                primaryText='HPA (6mo/12mo/24mo)'
                secondaryText={propertyDetailsData?.hpAsDisplayString || 'N/A'}
              />
              <PropertyDetailsIndicativeItem
                iconPath='M12,3C10.73,3 9.6,3.8 9.18,5H3V7H4.95L2,14C1.53,16 3,17 5.5,17C8,17 9.56,16 9,14L6.05,7H9.17C9.5,7.85 10.15,8.5 11,8.83V20H2V22H22V20H13V8.82C13.85,8.5 14.5,7.85 14.82,7H17.95L15,14C14.53,16 16,17 18.5,17C21,17 22.56,16 22,14L19.05,7H21V5H14.83C14.4,3.8 13.27,3 12,3M12,5A1,1 0 0,1 13,6A1,1 0 0,1 12,7A1,1 0 0,1 11,6A1,1 0 0,1 12,5M5.5,10.25L7,14H4L5.5,10.25M18.5,10.25L20,14H17L18.5,10.25Z'
                primaryText='Market Value'
                secondaryText={propertyDetailsData
                  ? formatValue(propertyDetailsData.lastAssessorMktValue, { type: 'money', decimals: 0 }) + " (" + propertyDetailsData.assessedYear + " assessed)"
                  : 'N/A'
                }
              />
              <PropertyDetailsIndicativeItem
                iconPath='M12,3L22,12H19V20H5V12H2L12,3M9.22,8.93C8.75,9.4 8.5,10.03 8.5,10.75C8.5,12.43 10.54,13.07 11.76,13.46C13.26,13.93 13.47,14.21 13.5,14.25C13.5,15 12.15,15 12,15V15C11.37,15 11.03,14.88 10.86,14.78C10.67,14.67 10.5,14.5 10.5,14H8.5C8.5,15.43 9.24,16.16 9.85,16.5C10.18,16.7 10.57,16.84 11,16.92V18H13V16.91C14.53,16.61 15.5,15.62 15.5,14.25C15.5,12.67 13.88,12.03 12.36,11.55C10.8,11.06 10.53,10.77 10.5,10.75C10.5,10.5 10.57,10.41 10.64,10.34C10.85,10.13 11.36,10 12,10V10C12.68,10 13.5,10.13 13.5,10.75H15.5C15.5,9.34 14.56,8.37 13,8.09V7H11V8.08C10.26,8.21 9.65,8.5 9.22,8.93Z'
                primaryText='Tax Amount'
                secondaryText={propertyDetailsData
                  ? formatValue(propertyDetailsData.lastTaxAmount, { type: 'money', decimals: 0 }) + " (as of " + propertyDetailsData.taxYear + ")"
                  : 'N/A'
                }
              />
            </Grid>
            <div className={classes.spacer} />
            <Typography variant="h6">Classification</Typography>
            <Grid container>
              <PropertyDetailsIndicativeItem
                iconPath='M16,9H19L14,16M10,9H14L12,17M5,9H8L10,16M15,4H17L19,7H16M11,4H13L14,7H10M7,4H9L8,7H5M6,2L2,8L12,22L22,8L18,2H6Z'
                primaryText='Gemstone'
                secondaryText={propertyDetailsData?.gemstone ?? 'N/A'}
              />
              <PropertyDetailsIndicativeItem
                iconPath='M11,13.5V21.5H3V13.5H11M12,2L17.5,11H6.5L12,2M17.5,13C20,13 22,15 22,17.5C22,20 20,22 17.5,22C15,22 13,20 13,17.5C13,15 15,13 17.5,13Z'
                primaryText='Demographic v2'
                secondaryText={propertyDetailsData?.amherstDemographicCluster_v2 ?? 'N/A'}
              />
              <PropertyDetailsIndicativeItem
                iconPath='M20,6H16V4A2,2 0 0,0 14,2H10C8.89,2 8,2.89 8,4V6H4C2.89,6 2,6.89 2,8V19A2,2 0 0,0 4,21H20A2,2 0 0,0 22,19V8A2,2 0 0,0 20,6M10,4H14V6H10V4M12,9A2.5,2.5 0 0,1 14.5,11.5A2.5,2.5 0 0,1 12,14A2.5,2.5 0 0,1 9.5,11.5A2.5,2.5 0 0,1 12,9M17,19H7V17.75C7,16.37 9.24,15.25 12,15.25C14.76,15.25 17,16.37 17,17.75V19Z'
                primaryText='Owner'
                secondaryText={propertyDetailsData?.ownerName ?? 'N/A'}
              />
              <PropertyDetailsIndicativeItem
                iconPath='M19.07,4.93C17.22,3 14.66,1.96 12,2C9.34,1.96 6.79,3 4.94,4.93C3,6.78 1.96,9.34 2,12C1.96,14.66 3,17.21 4.93,19.06C6.78,21 9.34,22.04 12,22C14.66,22.04 17.21,21 19.06,19.07C21,17.22 22.04,14.66 22,12C22.04,9.34 21,6.78 19.07,4.93M17,12V18H13.5V13H10.5V18H7V12H5L12,5L19.5,12H17Z'
                primaryText='PM ID'
                secondaryText={propertyDetailsData?.asgPropID ?? prospectProperty?.asgPropID ?? 'N/A'}
              />
            </Grid>
            <div className={classes.spacer} />
            <Typography variant="h6">Geography</Typography>
            <Grid container>
              <Grid item >
                <VerticalDetailsBox style={{ padding: 0, marginRight: 20 }}
                  containerType="div"
                  dataItem={propertyDetailsData || prospectProperty}
                  fields={[
                    {
                      dataField: "parcelNumber",
                      dataType: 'string',
                      caption: 'Parcel Number'
                    },
                    {
                      dataField: "fips",
                      dataType: 'string',
                      caption: 'FIPS'
                    },
                    {
                      dataField: "cbsa",
                      dataType: 'string',
                      caption: 'CBSA'
                    },
                    {
                      dataField: "cbsaName",
                      dataType: 'string',
                      caption: 'CBSA Name'
                    },
                    {
                      dataField: "censusRegion",
                      dataType: 'string',
                      caption: 'Census Region'
                    },
                    {
                      dataField: "censusTractGeoId",
                      dataType: 'string',
                      caption: 'Census Tract'
                    },
                    {
                      dataField: "censusTractName",
                      dataType: 'string',
                      caption: 'Census Tract Name'
                    },
                    {
                      dataField: "schoolDistrict",
                      dataType: 'string',
                      caption: 'School District'
                    },
                    {
                      dataField: "amherstRegion",
                      dataType: 'string',
                      caption: 'Amherst Region'
                    },
                    {
                      dataField: "communityName",
                      dataType: 'string',
                      caption: 'Community Name'
                    },
                    {
                      dataField: "isInOpportunityZone",
                      dataType: 'boolean',
                      caption: 'Opportunity Zone'
                    }
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm container>
                <PropertyDetailsMap propertyDetailsData={propertyDetailsData || prospectProperty} />
              </Grid>
            </Grid>

            <div className={classes.spacer} />
            {propertyDetailsData?.asgPropID != null &&
              <>
                <Typography variant="h6">History
                  <Typography display='inline' variant='subtitle2' component={'span'}> (PM ID {propertyDetailsData?.asgPropID})</Typography>
                </Typography>
                <PropertyHistory asgPropID={propertyDetailsData?.asgPropID} livingAreaSqFt={propertyDetailsData?.livingAreaSqFt ?? prospectProperty?.sqft} txnOppID={propertyDetailsData?.transactionOpportunityId || propertyDetailsData?.txnOppId || prospectProperty?.transactionOpportunityId} />
              </>
            }

            <div className={classes.spacer} />

            <Typography variant="h6">Schools</Typography>
            {schoolsLoading
              ? <div className={classes.loadingContainer}>
                <CircularProgress key={0} size={24} color="secondary" />
              </div>
              : <DataGrid
                  ref={schoolsGridRef}
                  dataSource={schoolsData}
                  className={"dxGridNoLineClamp"}
                  showBorders={false}
                  allowColumnResizing={true}
                  columnResizingMode={"widget"}
                  columnAutoWidth={true}
                >
                <Column dataField={'rating'} caption={''} cellRender={SchoolsCell_Rating} width={60} />
                <Column dataField={'name'} cellRender={SchoolsCell_Name} />
                <Column dataField={'type'} width={100} />
                <Column dataField={'subType'} width={120} />
                <Column dataField={'gradesServed'} caption={'Grades'} width={80} />
                <Column dataField={'distanceFromTarget'} caption={'Distance'} sortOrder={'asc'} cellRender={SchoolsCell_Distance} width={90} />
                <Paging defaultPageSize={10} />
                <Scrolling />
              </DataGrid>
            }
          </Stack>
      }
    </ModalPage>
  )
}

function mapState(state) {
  const { exploreMap, exploreData, exploreProspects, exploreLoading, appState } = state;
  const lastSelectedCluster = lastSelectedClusterSelector(state);
  return {
    explorePropertyDetailsIsOpen: exploreMap.propertyDetailsIsOpen,
    explorePropertyDetailsData: exploreData.propertyDetailsData,
    prospectsPropertyDetailsData: exploreProspects.propertyDetailsData,
    lastSelectedCluster,
    isMobile: appState.isMobile,
    isLoading: exploreLoading.isLoading
  };
}

const mapDispatch = {
  setExplorePropertyDetailsIsOpen,
  getPropertyDetailsDataForProspect,
  appNotify
}

export default compose(
  connect(mapState, mapDispatch)
)(PropertyDetailsPage);

